<template>
  <Modal
    v-model:visible="isVisible"
    size="md"
    :header="t('rateShop.rateSelectionReason.rateSelectionReason')"
    class="rate-selection-reason-modal"
    :dismissable-mask="false"
    @keypress.esc="onCancel"
  >
    <div class="rate-selection-reason-modal-rate-quotes">
      <KeyValuePair
        :label="t('rateShop.rateSelectionReason.lowestCostQuote')"
        :value="lowestPriceQuote.totalPrice"
        class="rate-quote"
      >
        <template #value>
          <RateQuoteDetailCard :rate-quote="lowestPriceQuote" />
        </template>
      </KeyValuePair>

      <KeyValuePair
        :label="t('rateShop.rateSelectionReason.youSelected')"
        :value="selectedQuote.totalPrice"
        class="rate-quote"
      >
        <template #value>
          <RateQuoteDetailCard :rate-quote="selectedQuote" />
        </template>
      </KeyValuePair>
    </div>

    <form class="rate-selection-reason-modal-form">
      <InputWrapper
        v-slot="{ inputId }"
        input-id="reason-type"
        :label="t('common.reasons')"
        :errors="v$.rateSelectionReasonType.$errors"
      >
        <RygenDropdown
          :id="inputId"
          v-model="internalValue.rateSelectionReasonType"
          :options="rateSelectionReasonTypes.data"
          :loading="rateSelectionReasonTypes.loading"
          append-to="body"
          option-label="name"
          class="reason-type-dropdown"
          panel-class="reason-type-dropdown-panel"
          :placeholder="t('common.reasons')"
        >
          <template #option="{ option }">
            <div class="reason-option">
              <div class="reason-name">{{ option.name }}</div>
              <div class="reason-description text-gray-darker">
                {{ option.description }}
              </div>
            </div>
          </template>
        </RygenDropdown>
      </InputWrapper>

      <InputWrapper
        v-slot="{ inputId }"
        input-id="reason-comments"
        :label="t('common.comments', 2)"
        :errors="v$.reasonComments.$errors"
        :optional="isReasonCommentOptional"
      >
        <Textarea
          :id="inputId"
          v-model.trim="internalValue.reasonComments"
          auto-resize
          :placeholder="t('common.comments', 2)"
        />
      </InputWrapper>
    </form>

    <template #footer>
      <RygenButton
        :label="t('common.cancel')"
        mode="tertiary"
        @click="onCancel"
      />
      <RygenButton
        v-tooltip="{
          value: t('rateShop.rateSelectionReason.submitDisabledTooltip'),
          disabled: !isSubmitDisabled,
        }"
        :label="t('common.submit')"
        icon="pi pi-check"
        mode="primary"
        :disabled="v$.$invalid || isSubmitDisabled"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  requiredIf,
  useToast,
  useTooltip,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { Loadable, RateSelectionReasonType } from '@rygen/rygen-ui/model';

import api from '@/api';
import { RateQuote, RateSelectionReason } from '@/model';

import RateQuoteDetailCard from '@/components/rateShop/RateQuoteDetailCard.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Textarea from 'primevue/textarea';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  lowestPriceQuote: {
    type: Object as PropType<RateQuote>,
    default: () => new RateQuote(),
  },
  selectedQuote: {
    type: Object as PropType<RateQuote>,
    default: () => new RateQuote(),
  },
});
const { lowestPriceQuote, selectedQuote } = toRefs(props);
const emit = defineEmits<{
  (event: 'confirm', rateSelectionReason: RateSelectionReason): void;
}>();

const { t } = useI18n();
const toast = useToast();
const { uid } = useUid('rate-selection-reason-modal');

const vTooltip = useTooltip();

const rateSelectionReasonTypes = ref<Loadable<RateSelectionReasonType[]>>({
  loading: false,
});

const internalValue: Ref<RateSelectionReason> = ref(new RateSelectionReason());

const rules = computed(() => ({
  rateSelectionReasonType: {
    required: required(t('common.reasons')),
  },
  reasonComments: {
    required: requiredIf(
      () => internalValue.value.rateSelectionReasonType?.mandatoryNotes,
      t('common.comments'),
    ),
  },
}));

const { v$, validate } = useValidations(internalValue, rules, uid);

const isReasonCommentOptional = computed(() => {
  return !internalValue.value.rateSelectionReasonType?.mandatoryNotes;
});

const isSubmitDisabled = computed(() => {
  return !isReasonCommentOptional.value && !internalValue.value.reasonComments;
});

const onCancel = () => {
  isVisible.value = false;
};

const onConfirm = async () => {
  if (!(await validate())) {
    return;
  }

  emit('confirm', internalValue.value);
  isVisible.value = false;
};

const loadRateSelectionReasonTypes = async () => {
  try {
    rateSelectionReasonTypes.value.loading = true;
    rateSelectionReasonTypes.value.data =
      await api.rateSelectionReason.findAllTypes();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    rateSelectionReasonTypes.value.error = e;
    console.error('Error loading rate selection reason types', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('rateShop.rateSelectionReason.rateSelectionReasonTypes', 2),
      }),
      error: e,
    });
  } finally {
    rateSelectionReasonTypes.value.loading = false;
  }
};

watch(isVisible, (newVal: boolean) => {
  v$.value.$reset();

  if (newVal) {
    internalValue.value = new RateSelectionReason();

    // Always fetch fresh types when opening
    loadRateSelectionReasonTypes();
  }
});
</script>

<style>
.rate-selection-reason-modal {
  .rate-selection-reason-modal-rate-quotes {
    .rate-quote {
      margin-bottom: var(--sp-3);

      & > .key-value-pair-label {
        font-size: var(--p-text-sm);
      }

      & > .key-value-pair-value {
        overflow: unset;
      }
    }
  }

  .rate-selection-reason-modal-form {
    .input-wrapper {
      max-width: 50%;
      margin-bottom: var(--sp-3);
    }

    .reason-type-dropdown {
      .reason-option {
        padding: var(--sp-2) 0;

        .reason-description {
          font-size: var(--sp-3);
          margin-bottom: var(--sp-1);
        }

        .reason-name {
          color: var(--p-gray-600);
        }
      }
    }
  }
}

.reason-type-dropdown-panel {
  max-width: var(--sp-6);

  .reason-option .reason-description {
    white-space: normal;
  }
}
</style>
